import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/lib/SiteContentProvider";
import SectionGlow from "@/components/site/SectionGlow";
import Reveal from "@/components/site/Reveal";

/**
 * AboutPreview — a short, founder-led teaser that routes into the full /about
 * story. Reuses site-content CMS fields (image, first paragraph, specialties).
 */
export const AboutPreview = () => {
    const { data } = useSiteContent();
    const a = data?.about || {};
    const name = a.founder_name || "Pranto";
    const lead = (a.paragraphs && a.paragraphs[0]) || "";
    const specialties = (a.specialties || []).slice(0, 4);

    return (
        <section id="about" className="pg-section pg-surface-ink" data-testid="home-about-preview" aria-label="About the studio">
            <SectionGlow tone="warm" />
            <div className="pg-container">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-14">
                    <Reveal className="lg:col-span-5">
                        <div
                            className="relative aspect-[4/5] overflow-hidden rounded-3xl border"
                            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                        >
                            <img
                                src={a.image}
                                alt={`${name}, Creative Director`}
                                className="pg-ambient-media h-full w-full object-cover"
                                style={{ objectPosition: a.image_position || "50% 30%" }}
                                loading="lazy"
                            />
                            <div
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(180deg, rgba(11,59,58,0.03) 0%, rgba(7,17,18,0.5) 100%)" }}
                            />
                            <div className="pg-noise" />
                            <div className="absolute bottom-5 left-5 right-5">
                                <div className="font-display text-2xl text-white">{name}</div>
                                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-white/80">
                                    Founder · Creative Director
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.08} className="lg:col-span-7">
                        <span className="pg-eyebrow">About · The Studio</span>
                        <h2 className="font-display mt-5 text-[clamp(2rem,4.6vw,3.2rem)] leading-[1.06]" style={{ color: "var(--pg-fg)" }}>
                            A decade of building brands that look <span className="pg-text-gold-static">unmistakably premium</span>.
                        </h2>
                        <p className="mt-6 max-w-xl text-base leading-relaxed sm:text-lg" style={{ color: "var(--pg-fg-muted)" }}>
                            {lead}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {specialties.map((s) => (
                                <span key={s} className="pg-chip">{s}</span>
                            ))}
                        </div>
                        <Link to="/about" data-testid="home-about-cta" className="pg-btn-primary mt-8 inline-flex">
                            Read the studio story
                            <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default AboutPreview;
